import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SearchBar from '@/components/feature/SearchBar';
import ResourceCard from '@/components/feature/ResourceCard';
import Pagination from '@/components/feature/Pagination';
import { filterResources, getPopularResources, paginate, totalPages } from '@/lib/catalog';
import { useSeo } from '@/hooks/useSeo';

const PAGE_SIZE = 6;

const TYPE_OPTIONS = [
  { value: 'all', label: '全部类型' },
  { value: 'video', label: '视频' },
  { value: 'software', label: '软件' },
  { value: 'tutorial', label: '教程' },
];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get('q') || '';
  const type = searchParams.get('type') || 'all';
  const page = Math.max(1, Number(searchParams.get('page') || '1'));

  const results = useMemo(
    () =>
      filterResources({
        keyword,
        category: type === 'all' ? undefined : type,
        sort: 'latest',
      }),
    [keyword, type],
  );

  const recommendations = useMemo(() => getPopularResources(3), []);

  useSeo({
    title: keyword ? `“${keyword}” 的搜索结果 - 免费91` : '全部免费资源 - 免费91',
    description: keyword
      ? `在免费91中搜索“${keyword}”，共找到 ${results.length} 个相关免费视频、软件与教程资源。`
      : '浏览免费91全部免费视频、软件与教程资源，支持按类型筛选与分页。',
    keywords: keyword ? `${keyword},免费${keyword},免费91` : '免费资源,免费视频,软件下载,免费教程,免费91',
  });

  const setType = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === 'all') next.delete('type');
    else next.set('type', value);
    next.delete('page');
    setSearchParams(next);
  };

  const goPage = (nextPage: number) => {
    const next = new URLSearchParams(searchParams);
    if (nextPage <= 1) next.delete('page');
    else next.set('page', String(nextPage));
    setSearchParams(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const pages = totalPages(results.length, PAGE_SIZE);
  const safePage = Math.min(page, pages);
  const items = paginate(results, safePage, PAGE_SIZE);

  return (
    <div className="w-full">
      <div className="w-full bg-background-100 border-b border-background-200">
        <div className="w-full px-4 md:px-8 py-8 md:py-10">
          <nav className="flex items-center gap-1.5 text-sm text-foreground-500 mb-4" aria-label="面包屑">
            <Link to="/" className="hover:text-primary-700 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-900">搜索结果</span>
          </nav>
          <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-5">
            {keyword ? `“${keyword}” 的搜索结果` : '浏览全部免费资源'}
          </h1>
          <div className="max-w-3xl">
            <SearchBar initialKeyword={keyword} initialCategory={type} />
          </div>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <p className="text-sm text-foreground-600">
            共找到 <span className="text-primary-700 font-medium">{results.length}</span> 个结果
            {keyword && <span className="text-foreground-500">（关键词：{keyword}）</span>}
          </p>
          <div className="flex items-center gap-2 overflow-x-auto f91-scroll-hidden">
            {TYPE_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setType(option.value)}
                className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer ${
                  type === option.value
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8">
          {items.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {items.map((resource) => (
                <ResourceCard key={resource.id} resource={resource} />
              ))}
            </div>
          ) : (
            <div>
              <div className="text-center py-16 rounded-lg bg-background-100 border border-background-200">
                <span className="inline-flex w-16 h-16 rounded-full bg-background-200 text-foreground-400 items-center justify-center text-3xl">
                  <i className="ri-search-eye-line"></i>
                </span>
                <h2 className="mt-5 font-heading font-semibold text-xl text-foreground-950">没有找到相关结果</h2>
                <p className="mt-2 text-foreground-600">
                  试试更换关键词，或切换结果类型。也可以看看下面这些热门资源。
                </p>
                <div className="mt-6 flex items-center justify-center gap-3">
                  <Link
                    to="/"
                    className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer"
                  >
                    <i className="ri-home-4-line"></i>
                    返回首页
                  </Link>
                  <button
                    type="button"
                    onClick={() => setType('all')}
                    className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md border border-background-300 text-foreground-800 hover:border-primary-300 text-sm font-medium transition-colors cursor-pointer"
                  >
                    <i className="ri-refresh-line"></i>
                    清除筛选
                  </button>
                </div>
              </div>

              <div className="mt-12">
                <h3 className="font-heading font-semibold text-lg text-foreground-950 mb-4">
                  热门资源推荐
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                  {recommendations.map((resource) => (
                    <ResourceCard key={resource.id} resource={resource} />
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <Pagination page={safePage} totalPages={pages} onChange={goPage} />
      </div>
    </div>
  );
}